import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { T } from './theme';
const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', color:T.inkSoft, fontWeight:600 };
const input = { border:`1px solid ${T.b200}`, borderRadius:8, padding:'8px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box', width:'100%' };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px', font:'inherit', fontWeight:600,
  fontSize:13, cursor:'pointer', background: primary ? T.rose : 'transparent',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });
const pill = (bg, fg, text) => <span style={{ background:bg, color:fg, borderRadius:99,
  padding:'2px 10px', fontSize:11.5, fontWeight:600, whiteSpace:'nowrap' }}>{text}</span>;

const STATUS = {
  draft:     { bg:T.infoBg, fg:T.info, text:'Draft' },
  generated: { bg:T.warningBg, fg:T.warning, text:'Generated' },
  sent:      { bg:T.passBg, fg:T.pass, text:'Sent' },
  void:      { bg:T.b100, fg:T.inkSoft, text:'Void' },
};
const TRIGGER = { manual:'Manual', retro:'Retro Bill', denial:'Plan request' };
// What can go in a packet. The same list as the Records tab on the Claims page (lib/rcmRecordsDesk.js PICKS): keep the two in step.
// sections = parts of the chart export; kinds = which notes inside them; att = kinds of uploaded chart documents that go with it.
const RECORD_PICKS = [
  { key:'consents', label:'Consents and intake paperwork', sections:['intake_consents'], kinds:['consents'], att:['aor'] },
  { key:'assessments', label:'Assessments (biopsychosocial, screenings)', sections:['assessments'], kinds:['assessment'] },
  { key:'treatment_plan', label:'Treatment plan', sections:['treatment_plan'], kinds:['treatment_plan'] },
  { key:'therapy', label:'Therapy and case management notes', sections:['clinical_notes'], kinds:['therapy'] },
  { key:'group', label:'Group notes', sections:['clinical_notes'], kinds:['group'] },
  { key:'doctor', label:"Doctor's notes (history and physical, psychiatric evaluation, follow-ups)", sections:['assessments', 'nursing_rounds'], kinds:['doctor'], att:['history_physical', 'psych_evaluation', 'followup_encounter'] },
  { key:'dr_orders', label:"Doctor's orders", sections:['dr_orders'], kinds:[] },
  { key:'nursing', label:'Nursing notes and rounds', sections:['nursing_rounds'], kinds:['nursing'] },
  { key:'vitals', label:'Vitals', sections:['vitals'], kinds:[] },
  { key:'mar', label:'Medication administration record', sections:['mar'], kinds:[] },
  { key:'counts', label:'Controlled substance count log', sections:['controlled_counts'], kinds:[] },
  { key:'discharge', label:'Discharge documentation', sections:['discharge', 'assessments', 'clinical_notes'], kinds:['discharge'] },
  { key:'ur', label:'Utilization review notes', sections:['ur'], kinds:['ur'] },
  { key:'authorizations', label:'Authorizations', sections:['authorizations'], kinds:[] },
  { key:'labs', label:'Labs and drug screens', sections:['labs'], kinds:[], att:['lab_result', 'ua'] },
  { key:'sign_in', label:'Group sign-in sheets', sections:['sign_in'], kinds:[] },
  { key:'ids', label:'Insurance card and ID', sections:[], kinds:[], att:['insurance', 'identification'] },
  { key:'outside', label:'Outside and hospital records', sections:[], kinds:[], att:['outside_records', 'hospital'] },
];
const RECORD_PRESETS = [
  ['Notes for the dates', ['therapy', 'group', 'doctor', 'nursing']],
  ['Medical', ['doctor', 'dr_orders', 'nursing', 'vitals', 'mar', 'labs']],
  ['Full chart', RECORD_PICKS.map(p => p.key)],
  ['Clear', []],
];
const uniq = a => [...new Set(a)];
// the export's section list for the ticked picks (same rule as the Claims page)
const sectionsForPicks = (on) => { const ps = RECORD_PICKS.filter(p => on[p.key]); const att = uniq(ps.flatMap(p => p.att || []));
  return uniq(ps.flatMap(p => p.sections)).concat(att.length ? ['attachments'] : [], uniq(ps.flatMap(p => p.kinds)).map(k => 'kind:' + k), att.map(c => 'att:' + c)); };
const OLD_SECTION = { intake_consents:'Intake and consents', assessments:'Assessments', treatment_plan:'Treatment plan', clinical_notes:'Clinical documentation', nursing_rounds:'Nursing and rounds',
  discharge:'Discharge documentation', ur:'Utilization review', vitals:'Vitals', mar:'Medication administration record', dr_orders:"Doctor's orders", controlled_counts:'Controlled substance count log',
  labs:'Labs', authorizations:'Authorizations', sign_in:'Group sign-in sheets', attachments:'Uploaded documents' };
// plain words for what a saved packet holds
const describeSections = (list) => { const a = Array.isArray(list) ? list : []; if (!a.length) return 'Full chart';
  if (!a.some(x => /^(kind|att):/.test(x))) return 'Face sheet, ' + a.map(x => OLD_SECTION[x] || x).join(', ');
  const has = x => a.includes(x); const got = RECORD_PICKS.filter(p => p.kinds.length ? p.kinds.every(k => has('kind:' + k)) : p.sections.length ? p.sections.every(has) : (p.att || []).every(c => has('att:' + c)));
  return 'Face sheet, ' + got.map(p => p.label.replace(/ \(.*\)$/, '')).join(', '); };
const D = v => v ? new Date(v).toLocaleDateString('en-US', { timeZone:'UTC' }) : '—';
const PT = v => v ? new Date(v).toLocaleString('en-US', { timeZone:'America/Los_Angeles' }) : '—';

export default function EMRRecords({ patientId }) {
  const [pkgs, setPkgs] = useState([]);
  const [consent, setConsent] = useState(null);
  const [nf, setNf] = useState(null);         // new-request form state
  const [genResult, setGenResult] = useState({}); // pkgId -> {notes_in_scope, flags}
  const [confirmSend, setConfirmSend] = useState(null);
  const [attachAttest, setAttachAttest] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/api/emr/patients/${patientId}/records-packages`),
        axios.get(`${API}/api/emr/patients/${patientId}/records-consent`),
      ]);
      setPkgs(p.data); setConsent(c.data); setErr('');
    } catch (e) {
      const s = e.response && e.response.status;
      setErr(s === 403 ? 'Your role does not have access to records releases. Use Print Face Sheet for ER/urgent handoffs.' : 'Could not load records requests');
    }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const payerRelease = consent && consent.releases && consent.releases.find(r => r.release_type === 'payer');

  const createDraft = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        trigger_type: nf.trigger_type,
        recipient_name: nf.recipient_name, recipient_org: nf.recipient_org || null,
        recipient_address: nf.recipient_address || null, recipient_phone: nf.recipient_phone || null,
        purpose: nf.purpose || null,
        date_from: nf.date_from || null, date_to: nf.date_to || null,
        location_id: nf.location_id ? Number(nf.location_id) : null,
        sections: sectionsForPicks(nf.sections),
      };
      if (nf.override_reason) body.consent_override_reason = nf.override_reason;
      await axios.post(`${API}/api/emr/patients/${patientId}/records-packages`, body);
      setNf(null); await load();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Create failed'); }
    setBusy(false);
  };

  const generate = async (id) => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/emr/records-packages/${id}/generate`);
      setGenResult(g => ({ ...g, [id]: { notes_in_scope: r.data.notes_in_scope, flags: r.data.flags } }));
      await load();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Generate failed'); }
    setBusy(false);
  };

  const [auditResult, setAuditResult] = useState({});
  // Licensed locations for this client's facility: choices for the address printed on a package (Chad 10/9).
  const [printLocs, setPrintLocs] = useState([]);
  useEffect(() => { axios.get(`${API}/api/emr/patients/${patientId}/print-locations`).then(r => setPrintLocs((r.data && r.data.data) || [])).catch(() => {}); }, [patientId]);
  const [auditBusy, setAuditBusy] = useState(null);
  const audit = async (id) => {
    setAuditBusy(id); setErr('');
    try {
      const r = await axios.post(`${API}/api/emr/records-packages/${id}/audit`);
      setAuditResult(a => ({ ...a, [id]: r.data }));
      await load();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Audit failed'); }
    setAuditBusy(null);
  };

  const send = async (id) => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/api/emr/records-packages/${id}/send`, { include_attestation: attachAttest });
      setConfirmSend(null); await load();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Send failed'); }
    setBusy(false);
  };

  const voidPkg = async (id, reason) => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/api/emr/records-packages/${id}/void`, { reason });
      setVoiding(null); await load();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || 'Void failed'); }
    setBusy(false);
  };

  const viewPdf = async (id) => {
    setBusy(true); setErr('');
    const w = window.open('', '_blank');
    try {
      const r = await axios.get(`${API}/api/emr/patients/${patientId}/casefile.pdf`,
        { params: { package_id: id }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (w) { w.location = url; }
      if (!w) { // popup blocked (Safari default) — download instead, nothing lost
        const a = document.createElement('a');
        a.href = url; a.download = 'records-package.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch { if (w) w.close(); setErr('Could not load package PDF'); }
    setBusy(false);
  };

  const startNew = () => setNf({ trigger_type:'manual', recipient_name:'', recipient_org:'',
    recipient_address:'', recipient_phone:'', purpose:'', date_from:'', date_to:'',
    sections: {},
    override_reason:'', location_id:'' });

  return (
    <div>
      {err && <div style={{ background:T.blockingBg, color:T.blocking, borderRadius:8,
        padding:'9px 14px', marginBottom:12, fontWeight:600, fontSize:13 }}>{err}</div>}

      {/* Consent state panel */}
      <div style={{ ...card, padding:'13px 18px', marginBottom:14,
        background: (consent && consent.has_consent) ? T.passBg : T.warningBg,
        border:`1px solid ${(consent && consent.has_consent) ? T.pass : T.warning}` }}>
        {consent === null ? 'Checking consent…' : consent.has_consent ? (
          <div style={{ color:T.pass, fontWeight:600, fontSize:13.5 }}>
            42 CFR Part 2 consent on file
            {payerRelease && <span> · Payer authorization (AOB): {payerRelease.recipient_name}</span>}
            {consent.releases.filter(r => r.release_type !== 'payer').map(r =>
              <span key={r.id}> · ROI: {r.recipient_name}</span>)}
          </div>
        ) : (
          <div style={{ color:T.warning, fontWeight:600, fontSize:13.5 }}>
            No records-scope release on file — packages will require a documented override to send (42 CFR Part 2)
          </div>
        )}
      </div>

      {/* New request */}
      {!nf && <button style={{ ...btn(true), marginBottom:14 }} onClick={startNew}>+ New Records Request</button>}
      {nf && (
        <div style={{ ...card, padding:18, marginBottom:14 }}>
          <strong style={{ display:'block', marginBottom:12 }}>New Records Request</strong>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div><div style={label}>Trigger</div>
              <select style={input} value={nf.trigger_type}
                onChange={e => setNf({ ...nf, trigger_type: e.target.value })}>
                <option value="manual">Manual</option>
              </select></div>
            <div><div style={label}>Purpose</div>
              <input style={input} value={nf.purpose} placeholder="e.g. Payer records request"
                onChange={e => setNf({ ...nf, purpose: e.target.value })} /></div>
            <div><div style={label}>Recipient Name *</div>
              <input style={input} value={nf.recipient_name}
                onChange={e => setNf({ ...nf, recipient_name: e.target.value })} /></div>
            <div><div style={label}>Recipient Organization</div>
              <input style={input} value={nf.recipient_org}
                onChange={e => setNf({ ...nf, recipient_org: e.target.value })} /></div>
            <div><div style={label}>Date From</div>
              <input type="date" style={input} value={nf.date_from}
                onChange={e => setNf({ ...nf, date_from: e.target.value })} /></div>
            <div><div style={label}>Date To</div>
              <input type="date" style={input} value={nf.date_to}
                onChange={e => setNf({ ...nf, date_to: e.target.value })} /></div>
            {printLocs.length > 1 && <div><div style={label}>Location on the records</div>
              <select style={input} value={nf.location_id || ''}
                onChange={e => setNf({ ...nf, location_id: e.target.value })}>
                <option value="">Automatic (from the authorization)</option>
                {printLocs.map(l => <option key={l.id} value={l.id}>{l.location_name}{l.address ? ' \u2014 ' + l.address : ''}</option>)}
              </select></div>}
          </div>
          <div style={{ marginTop:14 }}>
            <div style={label}>What to include (the face sheet is always included)</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
              {RECORD_PRESETS.map(([name, keys]) => (
                <button key={name} type="button" style={{ ...btn(false), padding:'6px 13px', color:T.ink }}
                  onClick={() => setNf({ ...nf, sections: Object.fromEntries(keys.map(k => [k, true])) })}>{name}</button>
              ))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(250px, 1fr))', gap:'10px 16px', marginTop:12 }}>
              {RECORD_PICKS.map(p => (
                <label key={p.key} style={{ fontSize:13, display:'flex', gap:8, alignItems:'flex-start', cursor:'pointer' }}>
                  <input type="checkbox" style={{ marginTop:2 }} checked={!!nf.sections[p.key]}
                    onChange={e => setNf({ ...nf, sections: { ...nf.sections, [p.key]: e.target.checked } })} /><span>{p.label}</span></label>
              ))}
            </div>
            <div style={{ fontSize:12, color:T.inkSoft, marginTop:10 }}>
              Notes, labs, vitals and sign-ins follow the dates above. The history and physical, psychiatric evaluation, insurance card and ID are included whatever the dates.</div>
          </div>
          {!(consent && consent.has_consent) && (
            <div style={{ marginTop:14 }}>
              <div style={label}>Consent Override Reason (required to send without a release)</div>
              <input style={input} value={nf.override_reason} placeholder="e.g. Court order dated ..."
                onChange={e => setNf({ ...nf, override_reason: e.target.value })} />
            </div>
          )}
          <div style={{ display:'flex', gap:8, marginTop:16 }}>
            <button style={btn(true)} disabled={busy || !nf.recipient_name || !RECORD_PICKS.some(p => nf.sections[p.key])} onClick={createDraft}>Create Draft</button>
            <button style={btn(false)} onClick={() => setNf(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Package list */}
      {pkgs.length === 0 && <div style={{ color:T.inkSoft, fontSize:13.5 }}>No records requests yet.</div>}
      {pkgs.map(p => {
        const st = STATUS[p.status] || STATUS.draft;
        const gr = genResult[p.id];
        const flags = gr ? gr.flags : (Array.isArray(p.unsigned_flags) ? p.unsigned_flags : []);
        return (
          <div key={p.id} style={{ ...card, padding:'14px 18px', marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <div>
                {pill(T.infoBg, T.info, TRIGGER[p.trigger_type] || p.trigger_type)}{' '}
                {pill(st.bg, st.fg, st.text)}
                <strong style={{ marginLeft:10 }}>{p.recipient_name}</strong>
                {p.recipient_org && <span style={{ color:T.inkSoft }}> · {p.recipient_org}</span>}
              </div>
              <div style={{ display:'flex', gap:6 }}>
                {p.status === 'draft' && <button style={btn(true)} disabled={busy} onClick={() => generate(p.id)}>Generate</button>}
                {p.status === 'generated' && <>
                  <button style={btn(false)} disabled={busy} onClick={() => generate(p.id)}>Re-generate</button>
                  <button style={{ ...btn(false), color:T.info, borderColor:T.info }}
                    disabled={busy || auditBusy === p.id}
                    onClick={() => audit(p.id)}>{auditBusy === p.id ? 'Auditing…' : 'AI Audit'}</button>
                  <button style={btn(true)} disabled={busy} onClick={() => setConfirmSend(p)}>Send</button>
                </>}
                {(p.status === 'generated' || p.status === 'sent') &&
                  <button style={btn(false)} disabled={busy} onClick={() => viewPdf(p.id)}>View PDF</button>}
                {p.status !== 'void' &&
                  <button style={{ ...btn(false), color:T.blocking, borderColor:T.blocking }}
                    onClick={() => setVoiding({ id: p.id, reason:'' })}>Void</button>}
              </div>
            </div>
            <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:6 }}>
              {p.date_from || p.date_to ? `Dates ${D(p.date_from)} – ${D(p.date_to)}` : 'Full record scope'}
              {' · '}{describeSections(p.sections)}
              {p.purpose && <span> · {p.purpose}</span>}
              {p.release_id && <span> · consent-linked</span>}
              {p.consent_override_reason && <span> · override: {p.consent_override_reason}</span>}
              {p.sent_at && <span> · Sent {PT(p.sent_at)}</span>}
              {p.status === 'void' && p.void_reason && <span> · void: {p.void_reason}</span>}
            </div>
            {gr && <div style={{ fontSize:12.5, marginTop:6, color:T.ink }}>
              {gr.notes_in_scope} note(s) in scope, versions frozen.</div>}
            {flags.length > 0 && (
              <div style={{ background:T.blockingBg, borderRadius:8, padding:'8px 12px', marginTop:8 }}>
                <div style={{ color:T.blocking, fontWeight:600, fontSize:12.5 }}>
                  {flags.length} documentation issue(s) — review before sending:</div>
                {flags.map((f, i) => <div key={i} style={{ color:T.blocking, fontSize:12.5 }}>
                  • {f.note_type || 'note'}: {f.issue}</div>)}
              </div>
            )}
            {(() => {
              const ar = auditResult[p.id] || p.audit_findings;
              if (!ar || !ar.findings) return null;
              const sev = { critical: { bg:T.blockingBg, fg:T.blocking }, warning: { bg:T.warningBg, fg:T.warning }, info: { bg:T.infoBg, fg:T.info } };
              const nCrit = ar.findings.filter(f => f.severity === 'critical').length;
              const nWarn = ar.findings.filter(f => f.severity === 'warning').length;
              return (
                <div style={{ border:`1px solid ${T.b200}`, borderRadius:8, padding:'10px 14px', marginTop:8 }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>
                    AI Chart Audit · {ar.documents_reviewed} document(s) · {nCrit} critical · {nWarn} warning
                    {p.audited_at && <span style={{ fontWeight:400, color:T.inkSoft }}> · {PT(p.audited_at)}</span>}
                  </div>
                  <div style={{ fontSize:12.5, color:T.inkSoft, marginBottom:8 }}>{ar.summary}</div>
                  {ar.findings.map((f, i) => {
                    const s = sev[f.severity] || sev.info;
                    return (
                      <div key={i} style={{ background:s.bg, borderRadius:6, padding:'7px 11px', marginBottom:5 }}>
                        <span style={{ color:s.fg, fontWeight:600, fontSize:11.5, textTransform:'uppercase' }}>
                          {f.severity} · {(f.area || '').replace('_',' ')}</span>
                        <div style={{ fontSize:12.5, marginTop:2 }}>{f.issue}</div>
                        {f.citation && <div style={{ fontSize:11.5, color:T.inkSoft, marginTop:2 }}>{f.citation}</div>}
                      </div>
                    );
                  })}
                  <div style={{ fontSize:11.5, color:T.inkSoft, marginTop:4 }}>
                    Advisory review — findings do not block sending. A human decides.</div>
                </div>
              );
            })()}
            {confirmSend && confirmSend.id === p.id && (
              <div style={{ background:T.warningBg, borderRadius:8, padding:'10px 14px', marginTop:8 }}>
                <div style={{ fontSize:13, marginBottom:8 }}>
                  Sending will mark this package disclosed and write a permanent entry in the accounting of
                  disclosures (42 CFR §2.25). {p.release_id ? 'Consent is linked.' :
                  (payerRelease && ['retro','denial'].includes(p.trigger_type)) ?
                  'The payer authorization (AOB) will be linked automatically.' :
                  p.consent_override_reason ? `Basis: documented override — ${p.consent_override_reason}` :
                  'No consent linked — send will be blocked without an override.'}
                </div>
                {(auditResult[p.id] || p.audit_findings) && (
                  <label style={{ display:'block', fontSize:12.5, marginBottom:8 }}>
                    <input type="checkbox" checked={attachAttest}
                      onChange={e => setAttachAttest(e.target.checked)} />
                    {' '}Attach Chart Integrity Statement (reports the AI review with honest finding counts)
                  </label>
                )}
                <button style={btn(true)} disabled={busy} onClick={() => send(p.id)}>Confirm Send</button>{' '}
                <button style={btn(false)} onClick={() => setConfirmSend(null)}>Cancel</button>
              </div>
            )}
            {voiding && voiding.id === p.id && (
              <div style={{ marginTop:8, display:'flex', gap:8 }}>
                <input style={{ ...input, width:320 }} placeholder="Void reason *" value={voiding.reason}
                  onChange={e => setVoiding({ ...voiding, reason: e.target.value })} />
                <button style={btn(true)} disabled={busy || !voiding.reason}
                  onClick={() => voidPkg(p.id, voiding.reason)}>Confirm Void</button>
                <button style={btn(false)} onClick={() => setVoiding(null)}>Cancel</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
